import React from 'react';
import { OutlineType, LayerType, renderTokens } from './ClassicCaptionPage';

interface TextRendererProps {
    text: string;
    outline: OutlineType;
    fontFamily: string;
    fontSize: number;
    color: string;
    background?: 'none' | 'white' | 'colored' | 'translucent';
    containerPadding?: { x: number; y: number };
    boxPadding?: { x: number; y: number };
    borderRadius?: number;
    currentSpokenWordColor?: string;
    primaryColor?: string;
}

// Helper functions for contrast checking
const getContrastColor = (hexColor: string): string => {
    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.7 ? 'black' : 'white';
};

// Function to convert RGB to luminance
function rgbToLuminance(r: number, g: number, b: number): number {
    const a = [r / 255, g / 255, b / 255];
    for (let i = 0; i < 3; i++) {
        if (a[i] <= 0.03928) {
            a[i] /= 12.92;
        } else {
            a[i] = Math.pow((a[i] + 0.055) / 1.055, 2.4);
        }
    }
    return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
}

// Function to calculate contrast ratio
function calculateContrastRatio(luminance1: number, luminance2: number): number {
    const light = Math.max(luminance1, luminance2);
    const dark = Math.min(luminance1, luminance2);
    return (light + 0.05) / (dark + 0.05);
}

// Function to check contrast and return result
function checkContrast(fixedColor: string, changeableColor: string): {
    sufficientContrast: boolean,
    replacementColor: string
} {
    // Convert color name to hex if needed
    const colorNameToHex = (color: string): string => {
        const colors: Record<string, string> = {
            black: "#000000",
            white: "#FFFFFF"
        };
        return colors[color.toLowerCase()] || color;
    };

    fixedColor = colorNameToHex(fixedColor);
    changeableColor = colorNameToHex(changeableColor);

    // Convert hex color to RGB
    function hexToRgb(hex: string): [number, number, number] {
        let r: number, g: number, b: number;
        if (hex.startsWith("#")) {
            hex = hex.slice(1);
        }
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
        return [r, g, b];
    }

    const [fixedR, fixedG, fixedB] = hexToRgb(fixedColor);
    const [changeableR, changeableG, changeableB] = hexToRgb(changeableColor);

    const luminanceFixed = rgbToLuminance(fixedR, fixedG, fixedB);
    const luminanceChangeable = rgbToLuminance(changeableR, changeableG, changeableB);

    const contrast = calculateContrastRatio(luminanceFixed, luminanceChangeable);

    if (contrast < 4.5) {
        const luminanceBlack = rgbToLuminance(0, 0, 0);
        const luminanceWhite = rgbToLuminance(255, 255, 255);
        const contrastWithBlack = calculateContrastRatio(luminanceFixed, luminanceBlack);
        const contrastWithWhite = calculateContrastRatio(luminanceFixed, luminanceWhite);

        return {
            sufficientContrast: false,
            replacementColor: contrastWithBlack > contrastWithWhite ? "black" : "white"
        };
    }

    return {
        sufficientContrast: true,
        replacementColor: changeableColor
    };
}

const getOutlineAndTextColorForStyle = (
    outline: OutlineType,
    background: 'none' | 'white' | 'colored' | 'translucent',
    currentSpokenWordColor: string,
    primaryColor: string
): { outlineColor: string|undefined; textColor: string } => {
    console.log('getOutlineAndTextColorForStyle - Input:', {
        outline,
        background,
        currentSpokenWordColor,
        primaryColor
    });

    const hasOutline = outline === '3d-shadow' || outline === 'stroke' || outline === '3d-stroke';
    
    let backgroundColor = background === 'none' ? undefined : 
        (background === "translucent" ? 'black' : 
        (background === 'white' ? 'white' : primaryColor));
    
    // For 3D stroke, we want high contrast between text and outline
    let outlineColor = hasOutline ? primaryColor : undefined;
    let textColor = currentSpokenWordColor || primaryColor;

    if (outline === '3d-stroke') {
        // Ensure high contrast between text and outline
        if (outlineColor === textColor) {
            textColor = getContrastColor(outlineColor || primaryColor);
        }
        // If we still don't have good contrast, adjust the outline color
        if (outlineColor && !checkContrast(textColor, outlineColor).sufficientContrast) {
            outlineColor = getContrastColor(textColor);
        }
    }

    console.log('getOutlineAndTextColorForStyle - Output:', {
        outlineColor,
        textColor,
        backgroundColor
    });

    return { outlineColor, textColor };
};

export const TextRenderer: React.FC<TextRendererProps> = ({
    text,
    outline,
    fontFamily,
    fontSize,
    color,
    background = 'none',
    containerPadding = { x: 0.1, y: 0.05 },
    boxPadding = { x: 0, y: 0 },
    borderRadius = 0,
    currentSpokenWordColor = '#000000',
    primaryColor = '#000000'
}) => {
    console.log('TextRenderer - Input props:', {
        text,
        outline,
        fontFamily,
        fontSize,
        color,
        background,
        currentSpokenWordColor,
        primaryColor
    });

    // Create a canvas to measure text width accurately
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
        console.error('TextRenderer - Failed to get canvas context');
        return null;
    }
    context.font = `${fontSize}px ${fontFamily}`;
    const textWidth = context.measureText(text).width;

    // Calculate container dimensions
    const effectsPadding = (() => {
        if (outline === '3d-shadow' || outline === '3d-stroke') {
            return fontSize * 0.15;
        } else if (outline === 'stroke') {
            return fontSize * 0.1;
        } else if (outline === 'shadow' || outline === 'glow') {
            return fontSize * 0.08;
        }
        return 0;
    })();

    const basePadding = containerPadding.x === 0
        ? (outline === 'none' ? 0 : fontSize * 0.05)
        : fontSize * 0.1;

    const svgPadding = basePadding + effectsPadding;
    const containerWidth = textWidth + 
        (containerPadding.x * 2) + 
        (svgPadding * 2) + 
        (fontSize * 0.1);

    // Calculate SVG height more precisely with tighter dimensions
    const svgHeight = (() => {
        let height = fontSize * 1.15;
        if (outline === '3d-stroke') {
            height = fontSize * 1.25;
        } else if (outline === '3d-shadow') {
            height = fontSize * 1.2;
        } else if (outline === 'shadow' || outline === 'stroke' || outline === 'glow') {
            height = fontSize * 1.15;
        }
        if (background !== 'none') {
            height += (containerPadding.y * 2);
        }
        return height;
    })();

    // Calculate centerY similar to ClassicCaptionPage
    const centerY = (() => {
        let center = svgHeight / 2;
        if (outline === '3d-stroke') {
            center = svgHeight / 2 - fontSize * 0.02;
        } else if (outline === '3d-shadow') {
            center = svgHeight / 2 - fontSize * 0.02;
        }
        return center;
    })();

    // Create a token structure similar to ClassicCaptionPage
    const token = {
        text,
        fromMs: 0,
        toMs: 1000,
        verticalOffset: 0
    };

    // Calculate positions array for the single token
    const positions = [{
        x: (containerWidth - textWidth) / 2,
        width: textWidth
    }];

    const processText = (t: string) => t;

    console.log('TextRenderer - Rendering setup:', {
        centerY,
        positions,
        effectsPadding,
        svgHeight,
        containerWidth
    });

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%'
        }}>
            <div style={{
                display: 'inline-block',
                backgroundColor: background !== 'none' ? 
                    (background === 'translucent' ? 'rgba(0, 0, 0, 0.7)' :
                    background === 'white' ? 'white' : primaryColor) : 'transparent',
                borderRadius: fontSize * borderRadius,
                paddingLeft: background !== 'none' ? containerPadding.x * fontSize : 0,
                paddingRight: background !== 'none' ? containerPadding.x * fontSize : 0,
                paddingTop: background !== 'none' ? containerPadding.y * fontSize : 0,
                paddingBottom: background !== 'none' ? containerPadding.y * fontSize : 0,
                overflow: 'hidden',
                textAlign: 'center',
                whiteSpace: 'nowrap'
            }}>
                <svg
                    style={{
                        width: 'auto',
                        height: 'auto',
                        display: 'block',
                    }}
                    width={containerWidth}
                    height={svgHeight}
                    viewBox={`0 0 ${containerWidth} ${svgHeight}`}
                    preserveAspectRatio="xMidYMid meet"
                >
                    {/* Shadow effect */}
                    {outline === 'shadow' && renderTokens(
                        'other',
                        [token],
                        positions,
                        centerY,
                        fontSize,
                        fontFamily,
                        processText,
                        'none',
                        outline,
                        background,
                        currentSpokenWordColor,
                        primaryColor,
                        0,
                        0,
                        30
                    )}

                    {/* 3D Shadow layers */}
                    {outline === '3d-shadow' && renderTokens(
                        'other',
                        [token],
                        positions,
                        centerY,
                        fontSize,
                        fontFamily,
                        processText,
                        'none',
                        outline,
                        background,
                        currentSpokenWordColor,
                        primaryColor,
                        0,
                        0,
                        30
                    )}

                    {/* 3D Stroke layers */}
                    {outline === '3d-stroke' && renderTokens(
                        'other',
                        [token],
                        positions,
                        centerY,
                        fontSize,
                        fontFamily,
                        processText,
                        'none',
                        outline,
                        background,
                        currentSpokenWordColor,
                        primaryColor,
                        0,
                        0,
                        30
                    )}

                    {/* Stroke layer */}
                    {outline === 'stroke' && renderTokens(
                        'other',
                        [token],
                        positions,
                        centerY,
                        fontSize,
                        fontFamily,
                        processText,
                        'none',
                        outline,
                        background,
                        currentSpokenWordColor,
                        primaryColor,
                        0,
                        0,
                        30
                    )}

                    {/* Glow effect */}
                    {outline === 'glow' && renderTokens(
                        'glow',
                        [token],
                        positions,
                        centerY,
                        fontSize,
                        fontFamily,
                        processText,
                        'none',
                        outline,
                        background,
                        currentSpokenWordColor,
                        primaryColor,
                        0,
                        0,
                        30
                    )}

                    {/* Main text - Always last */}
                    {renderTokens(
                        'main',
                        [token],
                        positions,
                        centerY,
                        fontSize,
                        fontFamily,
                        processText,
                        'none',
                        outline,
                        background,
                        currentSpokenWordColor,
                        primaryColor,
                        0,
                        0,
                        30
                    )}
                </svg>
            </div>
        </div>
    );
}; 